import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import RichTextField from 'Components/ComponentLibrary/Molecules/RichTextField';
import FileUploadButton from 'Components/UI/ActivityFeed/Shared/HouseholdFeed/MessageInput/FileUploadButton';
import { noop } from 'Helpers/PropTypes';
import fr from 'facet-rest';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { FileViewer }
  from 'Components/ComponentLibrary/Components/Uncategorized/FileViewer';
import { QueryVal, removeQuery } from 'Helpers/Query';
import { SendButton } from 'Components/ComponentLibrary/Components/Uncategorized/SendButton';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { DataValueMap } from 'Helpers/Testing';
import useMobileLayout from 'Hooks/useMobileLayout';
import { AttachmentsRebranded } from 'Components/ComponentLibrary/Components/Uncategorized/AttachmentsRebranded';

const useStyles = buildUseStyles('MemberMessageInput', (theme) => ({
  root: {},
  attachments: {
    padding: theme.spacing(1, 0),
    flexGrow: 1,
  },
  toolbarButton: {
    margin: 0,
  },
  sendButton: {
    padding: theme.spacing(1),
  },
  fileUploadButton: {
    boxSizing: 'content-box',
    height: 20,
    width: 20,
    padding: theme.spacing(1),
  },
  inputContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
  },
  readOnlyMessage: {
    padding: theme.spacing(2),
    whiteSpace: 'pre-line',
    textAlign: 'center',
    position: 'absolute',
    // Below is how to center an item vertically in absolute/relative positioning (overtop)
    // Move down 50% of the container height and then move back up 50% of the moved elements height to center perfectly
    top: '50%',
    transform: 'translate(0, -50%)',
    // Above is how to center an item vertically in absolute/relative positioning (overtop)
  },
}));

const MemberMessageInput = ({
  className,
  handleSubmit,
  handleChange,
  handleAttachmentDelete,
  messageSending,
  messageResource,
  onFileUploadSuccess,
  placeholder,
  inputMinHeight,
  inputMaxHeight,
  readOnly,
  value,
  attachments,
  dataTestAttributes,
  borderless,
  rounded,
  showAttachmentsInToolbar,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const [uploadProgress, setUploadProgress] = React.useState(null);
  const { displayErrorMessage } = useSnackbarContext();
  const isMobile = useMobileLayout();

  const hasAttachments = attachments.length > 0;
  const attachmentSelfLink = QueryVal('attachmentSelfLink');
  const disableActions = messageSending || readOnly;
  const disableSend = readOnly || value === '';
  const inputPlaceholder = readOnly ? '' : placeholder;
  const readOnlyMessage = isMobile ? 'This is a read-only message.\n If you have questions click "Compose" to start a new thread.'
    : 'This is a read-only message. If you have questions click "Compose" to start a new thread.';

  const SendButtonForInlineToolbar = (<SendButton
    key={'send-message-button'}
    className={classNames(classes.sendButton, classes.toolbarButton)}
    onClick={() => handleSubmit()}
    sending={messageSending}
    disabled={disableSend}
    dataTestAttributes={DataValueMap.init('send-message-button').build()}
  />);

  const FileUploadButtonForInlineToolbar = (<FileUploadButton
    key={'file-upload-button'}
    className={classes.toolbarButton}
    messageResource={messageResource}
    onStart={() => messageResource?.relationship(fr.relationships.attachments.name)?.link('related').url()}
    onSuccess={() => onFileUploadSuccess()}
    onFailure={(e) => displayErrorMessage(e, 'Failed to upload file')}
    uploadProgress={uploadProgress}
    setUploadProgress={setUploadProgress}
    disabled={disableActions}
    circularProgress
    ButtonProps={{
      className: classes.fileUploadButton,
    }}
  />);

  const inlineToolbar = [SendButtonForInlineToolbar];
  if (showAttachmentsInToolbar) {
    inlineToolbar.unshift(FileUploadButtonForInlineToolbar);
  }

  return (
    <Box className={classNames(className, classes.root)}>
      <Box
        className={classes.inputContainer}
        {...dataTestAttributes}
      >
        <RichTextField
          inputMinHeight={inputMinHeight}
          inputMaxHeight={inputMaxHeight}
          onChange={(val) => handleChange(val)}
          value={value}
          disabled={readOnly}
          rounded={rounded}
          borderless={borderless}
          inlineToolbar={!readOnly && inlineToolbar}
          bottomToolbar={hasAttachments && (
            <AttachmentsRebranded
              className={classes.attachments}
              attachmentResources={attachments}
              AttachmentProps={{
                onDelete: handleAttachmentDelete,
              }}
            />
          )}
          placeholder={inputPlaceholder}
        />
        {readOnly && (
          <Typography
            className={classes.readOnlyMessage}
            color={'mediumDarkGray'}
            variant={'h6'}
            weight={'light'}
            {...DataValueMap.init('read-only-message').build()}
          >
            {readOnlyMessage}
          </Typography>
        )}
      </Box>
      <FileViewer
        attachmentResources={attachments}
        isOpen={!!attachmentSelfLink}
        closeModal={() => removeQuery('attachmentSelfLink')}
        eventCategory={'Messages'}
      />
    </Box>
  );
};

MemberMessageInput.defaultProps = {
  className: '',
  messageSending: false,
  handleChange: noop,
  handleSubmit: noop,
  handleAttachmentDelete: noop,
  onFileUploadSuccess: noop,
  placeholder: 'Type here...',
  inputMinHeight: 36,
  inputMaxHeight: 250,
  readOnly: false,
  messageResource: null,
  value: '',
  attachments: [],
  dataTestAttributes: {},
  borderless: false,
  rounded: false,
  showAttachmentsInToolbar: true,
};

MemberMessageInput.propTypes = {
  className: PropTypes.string,
  messageSending: PropTypes.bool,
  messageResource: PropTypes.instanceOf(fr.Resource),
  handleChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  showAttachmentsInToolbar: PropTypes.bool,
  handleAttachmentDelete: PropTypes.func,
  onFileUploadSuccess: PropTypes.func,
  placeholder: PropTypes.string,
  inputMinHeight: PropTypes.number,
  inputMaxHeight: PropTypes.number,
  readOnly: PropTypes.bool,
  value: PropTypes.string,
  attachments: PropTypes.array,
  dataTestAttributes: PropTypes.object,
  borderless: PropTypes.bool,
  rounded: PropTypes.bool,
};

export { MemberMessageInput };
